import type {
  AfterFetchContext,
  UseFetchOptions,
  UseFetchReturn,
} from "@vueuse/core";
import { computed } from "vue";

import { loadTranslationFile, setHtmlLocale } from "@/i18n.ts";
import { useUserStore } from "@/stores/user.ts";
import type { PreSignUpData, SignUpData } from "@/types/user";
import { signupApiClient } from "@/utils/apiClients.ts";
import { auth0Init } from "@/utils/auth.ts";
import { i18nInstance, type Locale } from "@/utils/i18n.ts";
import {
  clearSubscriptionIntent,
  getSubscriptionIntent,
} from "@/utils/subscriptionIntent.ts";

const usePreSignUpData = (
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<PreSignUpData> => {
  const userStore = useUserStore();

  async function updateLanguageSettings(language: Locale): Promise<void> {
    const messages = await loadTranslationFile(language);
    const i18n = i18nInstance;

    i18n.global.setLocaleMessage(language, messages);
    //@ts-ignore -- somehow throws error, don't really know how to fix, suggested solutions haven't worked or led to more errors
    i18n.global.locale.value = language;
    setHtmlLocale(language);
  }

  function updateUserStore(preSignUpData: PreSignUpData): void {
    userStore.preSignUpFetchedAt = new Date();
    userStore.termsOfServiceAgreementRequired =
      preSignUpData.tosAgreementRequired;
    userStore.termsOfServiceVersionRequired = "v4";
    userStore.signUpRequired = preSignUpData.signupRequired;
    userStore.activationPending = preSignUpData.activationPending;
    userStore.deactivated = preSignUpData.deactivated;
    userStore.createDate = preSignUpData.createDate;
    userStore.activationDate = preSignUpData.activationDate;
    userStore.validToDate = preSignUpData.validToDate;
    userStore.preferences = {
      preferredCvdPage: preSignUpData.preferredCvdPage,
      preferredHomepage: preSignUpData.preferredHomepage,
      preferredLanguage: preSignUpData.preferredLanguage,
      suppressOnboardingTour: preSignUpData.suppressOnboardingTour,
    };
  }

  return signupApiClient("preSignup", {
    ...options,
    async afterFetch(ctx) {
      updateUserStore(ctx.data);
      await updateLanguageSettings(ctx.data.preferredLanguage);
      localStorage.setItem("preferredLanguage", ctx.data.preferredLanguage);
      return ctx;
    },
  }).json();
};

const useSignUpUser = (
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<SignUpData> => {
  const userStore = useUserStore();
  const subscriptionIntent = getSubscriptionIntent();
  const payload = subscriptionIntent
    ? { intent: subscriptionIntent }
    : undefined;
  return signupApiClient("signup", {
    ...options,
    async beforeFetch(ctx) {
      const token = auth0Init.idTokenClaims.value?.__raw; // Don't remove the Id token here as it needs to be send for the sign up!!!
      ctx.options.headers = {
        ...ctx.options.headers,
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      return ctx;
    },
    async afterFetch(ctx: AfterFetchContext) {
      if (ctx.response.status == 201) {
        userStore.signUpRequired = false;
        userStore.preSignUpFetchedAt = null;
        clearSubscriptionIntent();
      }
      return ctx;
    },
  })
    .post(payload)
    .json();
};

const useAcceptTermsOfService = (
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<string> => {
  const userStore = useUserStore();
  const url = computed(() => {
    return `termsOfServiceAgreement?tosVersion=${userStore.termsOfServiceVersionRequired || ""}`;
  });
  return signupApiClient(url, {
    ...options,
    afterFetch(ctx) {
      userStore.termsOfServiceAgreementRequired = false;
      return ctx;
    },
  })
    .post()
    .text();
};

export { useAcceptTermsOfService, usePreSignUpData, useSignUpUser };
